import { useEffect, useRef } from 'react';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

interface ExecutorDashboardTourProps {
  shouldRun: boolean;
}

const SEEN_KEY = 'eoe_executor_dashboard_tour_completed';
const SCROLL_CONTAINER_ID = 'main-content';

// Matches ExecutorNavigation.tsx DESKTOP_QUERY
const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;

/**
 * When multiple elements match a selector (e.g. desktop + mobile RoleSwitcher),
 * return the one that is actually visible (non-zero layout size).
 */
function findVisibleElement(selector: string): Element | string {
  const all = document.querySelectorAll(selector);
  for (const el of all) {
    if ((el as HTMLElement).offsetWidth > 0) return el;
  }
  return selector; // fallback to string if none visible
}

// CSS injected into <head> while the tour is mounted — mirrors PlannerDashboardTour styling
const TOUR_STYLES = `
  /* Popover container */
  .driver-popover {
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
      "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif !important;
    border-radius: 12px !important;
    border: 2px solid #0891b2 !important;
    padding: 20px 20px 16px !important;
    box-shadow: 0 12px 28px -4px rgba(0,0,0,0.18), 0 6px 14px -6px rgba(0,0,0,0.12) !important;
    max-width: 320px !important;
  }

  /* Title */
  .driver-popover-title {
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
      "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
    font-size: 15px !important;
    font-weight: 600 !important;
    color: #1c1917 !important;
    margin-bottom: 6px !important;
    line-height: 1.4 !important;
  }

  /* Body copy */
  .driver-popover-description {
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
      "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
    font-size: 13px !important;
    color: #57534e !important;
    line-height: 1.55 !important;
  }

  /* Footer / button row */
  .driver-popover-footer {
    margin-top: 16px !important;
    gap: 8px !important;
    align-items: center !important;
  }

  /* Step progress text  e.g. "1 of 5" */
  .driver-popover-progress-text {
    font-family: ui-sans-serif, system-ui, sans-serif !important;
    font-size: 12px !important;
    color: #a8a29e !important;
  }

  /* Previous button — outlined style */
  .driver-popover-prev-btn {
    font-family: ui-sans-serif, system-ui, sans-serif !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #0891b2 !important;
    background: transparent !important;
    border: 1px solid #0891b2 !important;
    border-radius: 6px !important;
    padding: 6px 14px !important;
    cursor: pointer !important;
    text-shadow: none !important;
  }
  .driver-popover-prev-btn:hover {
    background: #ecfeff !important;
  }
  .driver-popover-prev-btn:focus-visible {
    outline: 2px solid #0891b2 !important;
    outline-offset: 2px !important;
  }

  /* Next / Finish button — filled cyan-600 */
  .driver-popover-next-btn {
    font-family: ui-sans-serif, system-ui, sans-serif !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #fff !important;
    background: #0891b2 !important;
    border: 1px solid #0891b2 !important;
    border-radius: 6px !important;
    padding: 6px 14px !important;
    cursor: pointer !important;
    text-shadow: none !important;
  }
  .driver-popover-next-btn:hover {
    background: #0e7490 !important;
    border-color: #0e7490 !important;
  }
  .driver-popover-next-btn:focus-visible {
    outline: 2px solid #0891b2 !important;
    outline-offset: 2px !important;
  }

  /* Close (×) button */
  .driver-popover-close-btn {
    color: #78716c !important;
  }
  .driver-popover-close-btn:hover {
    color: #1c1917 !important;
  }
`;

const getSteps = (): DriveStep[] => {
  const mobile = isMobile();

  return [
    {
      element: '[data-tour="executor-dashboard-screen"]',
      popover: {
        title: 'Welcome to the Executor Dashboard',
        description:
          'Welcome to the Executor Dashboard. This space is here to help you better understand the wishes, records, documents, and important information that have been shared with you. Since the planner is still alive, the dashboard is currently available as a read-only experience for planning and preparation.',
        side: 'over',
        align: 'start',
      },
    },
    {
      element: mobile
        ? '[data-tour="executor-mobile-nav-trigger"]'
        : '[data-tour="executor-left-nav"]',
      popover: {
        title: 'Navigate Your Plan',
        description:
          'Use the navigation menu to review records, documents, assets, dependents, and other planning information that has been shared with you. You can return here at any time to move between sections of the estate plan.\n\nAt the bottom, you can also access your account settings and support.',
        side: mobile ? 'bottom' : 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="executor-plan-summary-tiles"]',
      popover: {
        title: 'Review Planning Information',
        description:
          'Each section contains information completed by the planner. You can review personal wishes, financial information, important documents, and other details that may help you better understand the estate plan over time.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: '[data-tour="executor-expandable-section"]',
      popover: {
        title: 'Explore Shared Details',
        description:
          'Open sections to review additional details, instructions, and information that have been shared with you. Some sections may include documents, contacts, account information, or personal guidance from the planner.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // RoleSwitcher uses data-tour="planner-role-switcher" — same button in both layouts
      element: findVisibleElement('[data-tour="planner-role-switcher"]'),
      popover: {
        title: 'Profile and Roles',
        description:
          'If you have multiple roles, such as Executor, Planner or Beneficiary, you can switch between dashboards at any time using the role selector in the top right corner.',
        side: 'bottom',
        align: 'end',
      },
    },
  ];
};

export default function ExecutorDashboardTour({ shouldRun }: ExecutorDashboardTourProps) {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  // Inject app-matched CSS for the Driver.js popover while the tour component is mounted
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'executor-tour-styles';
    styleEl.textContent = TOUR_STYLES;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  useEffect(() => {
    if (!shouldRun) return;

    let timerId: ReturnType<typeof setTimeout>;

    const rafId = requestAnimationFrame(() => {
      timerId = setTimeout(() => {
        // Reset scroll position so Step 1 always starts at the top
        const main = document.getElementById(SCROLL_CONTAINER_ID);
        main?.scrollTo({ top: 0, left: 0 });

        // Filter out steps whose target element is not in the DOM or not visible
        const steps = getSteps().filter((step) => {
          if (!step.element) return true;
          if (step.element instanceof Element) return (step.element as HTMLElement).offsetWidth > 0;
          return document.querySelector(step.element) !== null;
        });

        if (steps.length === 0) {
          localStorage.setItem(SEEN_KEY, 'true');
          return;
        }

        const driverInstance = driver({
          showProgress: true,
          animate: true,
          overlayOpacity: 0.6,
          allowClose: true,
          stagePadding: 6,
          stageRadius: 8,
          disableActiveInteraction: true,
          nextBtnText: 'Next',
          prevBtnText: 'Previous',
          doneBtnText: 'Finish',
          steps,
          onDestroyed: () => {
            localStorage.setItem(SEEN_KEY, 'true');
          },
        });

        driverRef.current = driverInstance;
        driverInstance.drive();
      }, 150);
    });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, [shouldRun]);

  return null;
}
